import { IconEditOrders } from 'assets/DesignSystem/SVGComponents';
import { CopyIcon } from 'assets/images';
import { IProductDelivery } from 'types/orders';
import { copyTextToClipboard } from 'views/TrackingOrder/trackingOrder.utils';

interface IShipmentProductRow {
  product: IProductDelivery;
  productNumber: number;
  totalProducts: number;
  status?: string;
  onSerialClick?: () => void;
}

/**
 * Retorna el texto del estado del producto formateado.
 * @param status - Estado del producto
 * @returns Texto del estado
 */
const getStatusText = (status?: string): string => {
  if (!status) return 'Sin estado';

  const statusMap: Record<string, string> = {
    preparation: 'Preparación',
    confirmed: 'Confirmado',
    delivered: 'Entregado',
    in_transit: 'En tránsito',
    cancelled: 'Cancelado'
  };

  return statusMap[status.toLowerCase()] || status;
};

/**
 * Retorna la clase CSS para el badge de estado.
 * @param status - Estado del producto
 * @returns Clase CSS del estado
 */
const getStatusClass = (status?: string): string => {
  if (!status) return 'default';

  const statusClassMap: Record<string, string> = {
    preparation: 'preparation',
    confirmed: 'confirmed',
    delivered: 'delivered',
    in_transit: 'inTransit',
    cancelled: 'cancelled'
  };

  return statusClassMap[status.toLowerCase()] || 'default';
};

/**
 * Genera las especificaciones del producto en formato legible.
 * @param pills - Objeto con las especificaciones
 * @returns String formateado con las specs
 */
const formatProductSpecs = (pills?: Record<string, string>): string => {
  if (!pills || Object.keys(pills).length === 0) return '';

  return Object.values(pills).filter(Boolean).join(' - ');
};

/**
 * Fila de producto dentro del drawer de detalle de envío.
 * Muestra información del producto, su estado y enlace para ver/editar seriales.
 */
const ShipmentProductRow = ({ product, productNumber, totalProducts, status, onSerialClick }: IShipmentProductRow) => {
  const specs = formatProductSpecs(product.pills);
  const statusText = getStatusText(status);
  const statusClass = getStatusClass(status);
  const hasSerial = Boolean(product.serial || (product.serials && product.serials.length > 0));
  const serialLinkText = hasSerial ? 'Ver/editar seriales' : 'Agregar serial';

  /**
   * Maneja el click en el enlace de seriales.
   */
  const handleSerialClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onSerialClick?.();
  };

  return (
    <div className="shipmentProductRow">
      <div className="productHeader">
        <span className="productCounter">
          Producto {productNumber} de {totalProducts}
        </span>
      </div>

      <div className="productContent">
        <div className="unitColumn">
          <span className="unitLabel">Und.</span>
          <span className="quantity">{product.unit || 1}</span>
        </div>

        <div className="productColumn">
          <span className="productLabel">Producto</span>
          <div className="productNameSection">
            <span className="productName">{product.name || 'Sin nombre'}</span>
            {product.nudosCareName && <span className="nudosCare">{product.nudosCareName}</span>}
            {specs && <span className="productSpecs" title={specs}>{specs}</span>}
            <button className="copyButton" onClick={() => copyTextToClipboard(specs)} aria-label="Copiar specs">
              <CopyIcon />
            </button>
          </div>
          <div className="skuSection">
            <span className="skuLabel">SKU Catálogo: {product.sku || 'Sin SKU'}</span>
            <button className="serialLink" onClick={handleSerialClick}>
              {serialLinkText} <IconEditOrders />
            </button>
          </div>
        </div>

        <div className="statusColumn">
          <span className="statusLabel">Estado</span>
          <span className={`statusBadge ${statusClass}`}>{statusText}</span>
        </div>
      </div>
    </div>
  );
};

export default ShipmentProductRow;
